// In memory cache
// Just wrap a high quality LRU implementation

import _ from 'lodash';
import LRU from 'lru-cache';

import {MultiSetEntry, Cache, CacheKey} from './types';

export interface InMemoryCacheOpts<K extends CacheKey, IK extends any> {
  maxElements: number;
  keyFn: (oldKey: K) => IK;
}

export class InMemoryCache<K extends CacheKey, IK extends any>
  implements Cache
{
  private readonly lru: LRU<IK, any>;
  private readonly opts: InMemoryCacheOpts<K, IK>;

  // TODO(np): This is very naively implemented.  We need
  // a proper length function and then to decide on a reasonable
  // size for this cache
  constructor(public optsIn?: Partial<InMemoryCacheOpts<K, IK>>) {
    this.opts = _.defaults({}, optsIn, {
      maxElements: 20000,
      keyFn: (k: K) => k as any,
    });
    this.lru = new LRU({
      max: this.opts.maxElements,
      // TODO(np): write proper length fn
      length: (n, key) => 1,
      maxAge: 1000 * 60 * 60 * 24, // 1 day
    });
  }

  get(key: K): Promise<any> {
    const value = this.lru.get(this.opts.keyFn(key));
    // console.log(`[CG Cache] >>> Get ${key}: ${JSON.stringify(value, null, 2)}`);
    return Promise.resolve(value);
  }

  getMulti(keys: K[]): Promise<any[]> {
    return Promise.all(keys.map(k => this.get(k)));
  }

  set(key: K, value: any, ttlSeconds?: number): Promise<boolean> {
    // console.log(`[CG Cache] <<< Set ${key}`);
    return Promise.resolve(
      this.lru.set(
        this.opts.keyFn(key),
        value,
        ttlSeconds ? ttlSeconds * 1000 : undefined
      )
    );
  }

  async setMulti(entries: Array<MultiSetEntry<K>>): Promise<boolean> {
    const result = await Promise.all(
      entries.map(e => this.set(e.key, e.value, e.ttlSeconds))
    );
    return result.every(r => !!r);
  }

  del(key: K): Promise<void> {
    return Promise.resolve(this.lru.del(this.opts.keyFn(key)));
  }

  has(key: K): Promise<boolean> {
    return Promise.resolve(this.lru.has(this.opts.keyFn(key)));
  }

  hasMulti(keys: K[]): Promise<boolean[]> {
    return Promise.all(keys.map(k => this.has(k)));
  }

  reset(): Promise<void> {
    return Promise.resolve(this.lru.reset());
  }
}
